import type { HTMLAttributes } from 'react';
import type { RosterSettings } from '../../../../types/scholarships';
import { HANDEDNESS_LABELS, paletteColorVar, traitColorKey, type Handedness } from './rosterPalette';
import { IndicatorDot, TraitNamePill } from './RosterPlayerMarks';
import './rosterManagement.css';

export type RosterLegendSection = 'handedness' | 'indicators' | 'traits';

interface Props extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  settings: RosterSettings;
  sections: readonly RosterLegendSection[];
  /** Which hitting sides to list. The lineups only name LHH and RHH, like the mockup's key. */
  sides?: readonly Handedness[];
}

const SECTION_TITLES: Record<RosterLegendSection, string> = {
  handedness: 'Name color = handedness',
  indicators: 'Player indicators (dots)',
  traits: 'Evaluation tags (pill)',
};

/**
 * The key under a roster view: what a name colour, a dot and a pill mean on THIS board. It reads
 * the board's own settings, so a coach who made left-handers blue sees blue here too.
 */
export const RosterLegend = ({
  settings,
  sections,
  sides = ['left', 'right', 'switch'],
  className,
  ...rest
}: Props) => (
  <section
    className={['roster-legend', className].filter(Boolean).join(' ')}
    aria-label="Legend"
    {...rest}
  >
    {sections.map((section) => (
      <div className="roster-legend__group" key={section}>
        <h3 className="roster-legend__title">{SECTION_TITLES[section]}</h3>
        <ul className="roster-legend__items">
          {section === 'handedness' &&
            sides.map((side) => (
              <li key={side} className="roster-legend__item">
                <span
                  className="roster-legend__name-sample"
                  style={{ color: paletteColorVar(settings.handednessColors[side]) }}
                >
                  {HANDEDNESS_LABELS[side]}
                </span>
              </li>
            ))}
          {section === 'indicators' &&
            (settings.indicators.length === 0 ? (
              <li className="roster-legend__item roster-legend__item--empty">None set up</li>
            ) : (
              settings.indicators.map((indicator) => (
                <li key={indicator.id} className="roster-legend__item">
                  <IndicatorDot indicator={indicator} />
                  {indicator.name}
                </li>
              ))
            ))}
          {section === 'traits' &&
            (settings.evaluationTraits.length === 0 ? (
              <li className="roster-legend__item roster-legend__item--empty">None set up</li>
            ) : (
              settings.evaluationTraits.map((trait) => (
                <li key={trait.id} className="roster-legend__item">
                  <TraitNamePill name={trait.name} colorKey={traitColorKey(trait.id, settings)} />
                </li>
              ))
            ))}
        </ul>
      </div>
    ))}
  </section>
);
